(() => {
  const pickerSelector = '.shopify-section--main-product variant-picker';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const svgNamespace = 'http://www.w3.org/2000/svg';
  let stopped = new URL(window.location.href).searchParams.has('variant');
  let advancing = false;
  let ring = null;
  let miniRing = null;
  const miniSlides = new WeakMap();
  let formId = null;
  let pendingTimeout = null;

  const isPaused = () => document.hidden || Boolean(document.querySelector('cart-drawer[open]'));

  const syncPause = () => {
    ring?.classList.toggle('is-paused', isPaused());
    miniRing?.classList.toggle('is-paused', isPaused());
  };

  const removeRing = () => {
    ring?.remove();
    miniRing?.remove();
    ring = null;
    miniRing = null;
  };

  const stop = () => {
    stopped = true;
    advancing = false;
    window.clearTimeout(pendingTimeout);
    removeRing();
  };

  const getControls = (picker) => {
    const fieldset = picker?.querySelector('[data-ft-color-option]');
    if (!fieldset) return [];

    return [...fieldset.querySelectorAll('input[type="radio"][data-option-position]')]
      .map((input) => ({ input, label: fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`) }))
      .filter(({ input, label }) =>
        label && !input.disabled && !input.dataset.productUrl &&
        !label.hidden && !label.classList.contains('is-disabled') &&
        getComputedStyle(label).display !== 'none'
      );
  };

  const makeRing = (label, duration) => {
    const width = label.offsetWidth + 8;
    const height = label.offsetHeight + 8;
    if (width < 12 || height < 12) return null;

    const radius = Math.min(
      Math.max((Number.parseFloat(getComputedStyle(label).borderTopLeftRadius) || 0) + 4, 5),
      (height - 4) / 2
    );
    const svg = document.createElementNS(svgNamespace, 'svg');
    svg.setAttribute('class', 'ft-color-rotation-ring');
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.setProperty('--ft-color-rotation-duration', `${duration}ms`);

    for (const kind of ['track', 'progress']) {
      const outline = document.createElementNS(svgNamespace, 'rect');
      outline.setAttribute('class', `ft-color-rotation-ring__${kind}`);
      outline.setAttribute('x', '2');
      outline.setAttribute('y', '2');
      outline.setAttribute('width', String(width - 4));
      outline.setAttribute('height', String(height - 4));
      outline.setAttribute('rx', String(radius));
      outline.setAttribute('pathLength', '100');
      svg.append(outline);
    }

    if (isPaused()) svg.classList.add('is-paused');
    label.append(svg);
    return svg;
  };

  const syncMini = (controls) => {
    const mini = document.querySelector('.shopify-section--main-product [data-ft-color-mini]');
    if (!mini) return null;

    const buttons = [...mini.querySelectorAll('[data-ft-mini-option-id]')];
    buttons.forEach((button) => miniSlides.get(button)?.cancel());
    const previousCurrent = buttons.find((button) => button.classList.contains('is-current'));
    const previousRects = new Map(
      buttons.filter((button) => !button.hidden).map((button) => [button, button.getBoundingClientRect()])
    );
    buttons.forEach((button) => {
      button.hidden = true;
      button.classList.remove('is-previous', 'is-current', 'is-next');
      button.removeAttribute('aria-current');
    });

    const available = controls
      .map(({ input }) => ({
        input,
        button: buttons.find((button) => button.dataset.ftMiniOptionId === input.value)
      }))
      .filter(({ button }) => button);
    const index = available.findIndex(({ input }) => input.checked);
    if (available.length < 2 || index < 0) {
      mini.hidden = true;
      return null;
    }

    const show = ({ button }, position) => {
      button.hidden = false;
      button.classList.add(`is-${position}`);
      if (position === 'current') button.setAttribute('aria-current', 'true');
    };
    if (available.length > 2) show(available[(index - 1 + available.length) % available.length], 'previous');
    show(available[index], 'current');
    const next = available[(index + 1) % available.length];
    show(next, 'next');
    mini.hidden = false;

    if (previousCurrent && previousCurrent !== available[index].button && !reducedMotion.matches && mini.offsetWidth) {
      const previousIndex = available.findIndex(({ button }) => button === previousCurrent);
      const forwardSteps = (index - previousIndex + available.length) % available.length;
      const backwardsSteps = (previousIndex - index + available.length) % available.length;
      const direction = forwardSteps <= backwardsSteps ? 1 : -1;
      const currentRect = available[index].button.getBoundingClientRect();
      const nextRect = next.button.getBoundingClientRect();
      const slotWidth = Math.abs(nextRect.left - currentRect.left) || currentRect.width;

      buttons.filter((button) => !button.hidden).forEach((button) => {
        const oldRect = previousRects.get(button);
        const newRect = button.getBoundingClientRect();
        const offset = oldRect ? oldRect.left - newRect.left : direction * slotWidth;
        if (Math.abs(offset) < 1 && oldRect) return;

        miniSlides.set(button, button.animate([
          { transform: `translateX(${offset}px)`, opacity: oldRect ? 1 : 0 },
          { transform: 'translateX(0)', opacity: 1 }
        ], { duration: 260, easing: 'cubic-bezier(.2, .7, .2, 1)' }));
      });
    }
    return next.button;
  };

  const refresh = () => {
    removeRing();
    const picker = document.querySelector(pickerSelector);
    const controls = getControls(picker);
    const nextMiniButton = syncMini(controls);
    formId = picker?.getAttribute('form-id');
    if (stopped || advancing || reducedMotion.matches || !picker?.hasAttribute('data-ft-auto-cycle-colors') || controls.length < 2) return;

    const selected = controls.find(({ input }) => input.checked);
    if (!selected) return;

    const duration = Math.max(3000, Math.min(Number(picker.dataset.ftCycleMs) || 5000, 15000));
    ring = makeRing(selected.label, duration);
    miniRing = nextMiniButton ? makeRing(nextMiniButton, duration) : null;
    ring?.querySelector('.ft-color-rotation-ring__progress')?.addEventListener('animationend', () => {
      if (stopped || advancing) return;
      if (isPaused()) {
        refresh();
        return;
      }

      const currentPicker = document.querySelector(pickerSelector);
      const currentControls = getControls(currentPicker);
      const index = currentControls.findIndex(({ input }) => input.checked);
      if (currentControls.length < 2 || index < 0) return;

      const next = currentControls[(index + 1) % currentControls.length];
      advancing = true;
      removeRing();
      pendingTimeout = window.setTimeout(stop, 15000);
      // Select through the native change handler without an outside click that
      // would dismiss the cart's focus trap.
      next.input.checked = true;
      next.input.dispatchEvent(new Event('change', { bubbles: true }));
    }, { once: true });
  };

  const isShopperChoice = (target) => target instanceof Element && Boolean(
    target.closest('.shopify-section--main-product .variant-picker__option, .shopify-section--main-product .product-info__quantity-selector, .shopify-section--main-product .product-info__buy-buttons, .shopify-section--main-product .product-quick-add, .shopify-section--main-product .ft-color-mini')
  );

  document.addEventListener('pointerdown', (event) => {
    if (event.isTrusted && isShopperChoice(event.target)) stop();
  }, true);
  document.addEventListener('click', (event) => {
    if (event.isTrusted && isShopperChoice(event.target)) stop();
  }, true);
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const miniButton = event.target.closest('.shopify-section--main-product [data-ft-mini-option-id], .shopify-section--main-product [data-ft-mini-next]');
    if (!miniButton) return;

    const controls = getControls(document.querySelector(pickerSelector));
    const selectedIndex = controls.findIndex(({ input }) => input.checked);
    if (selectedIndex < 0) return;

    const next = miniButton.hasAttribute('data-ft-mini-next')
      ? controls[(selectedIndex + 1) % controls.length]
      : controls.find(({ input }) => input.value === miniButton.dataset.ftMiniOptionId);
    if (next && !next.input.checked) next.input.click();
  });
  document.addEventListener('keydown', (event) => {
    if (event.isTrusted && ['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) && isShopperChoice(event.target)) stop();
  }, true);
  document.addEventListener('change', (event) => {
    if (!advancing && isShopperChoice(event.target)) stop();
  }, true);

  document.addEventListener('variant:change', (event) => {
    // A hidden input named "id" shadows form.id in this theme, so read the attribute.
    if (event.target?.getAttribute?.('id') !== formId) return;
    if (stopped) {
      window.setTimeout(refresh, 0);
      return;
    }
    if (!advancing) {
      stop();
      window.setTimeout(refresh, 0);
      return;
    }
    advancing = false;
    window.clearTimeout(pendingTimeout);
    window.setTimeout(refresh, 0);
  });

  document.addEventListener('visibilitychange', syncPause);
  document.addEventListener('toggle', (event) => {
    if (event.target instanceof Element && event.target.matches('cart-drawer')) {
      // A fresh countdown on close also handles a timer ending as the cart opens.
      refresh();
    }
  }, true);
  document.addEventListener('shopify:section:load', () => window.setTimeout(refresh, 0));
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) stop();
    else refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();
